import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BankIcon from '../../../static/assets/svg/account_balance.svg';

import { useTranslation } from 'react-i18next';
import AccountNameHolder from '../form-inputs/account-name-holder-input/AccountNameHolderInput';
import SwiftInput from '../form-inputs/swift-input/SwiftInput';
import AccountNumberInput from '../form-inputs/account-number-input/AccountNumberInput';
import BrushIcon from '../../../static/assets/svg/brush.svg';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  BusinessProfileBankDetails,
  selectBusinessProfileBankDetails,
  selectBusinessProfileStatus,
  updateBusinessProfileBankDetails,
} from '../../slices/business-profile/businessProfileSlice';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import styles from './SupplierDetailsCard.module.scss';
import CurrencySelector from '../currency-selector/CurrencySelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import CompanyContactItem from '../../pages/settings/components/CompanyContactItem/CompanyContactItem';
import { Supplier } from '../../models/supplier.model';
import { updateCustomers } from '../../slices/customers/customersSlice';
import OrganizationIcon from '../../../static/assets/svg/organization.svg';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';
import { AccountCircleOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { NWClient } from '../../client/NWClient';
import { updateSupplier } from '../../slices/suppliers/suppliersSlice';

interface CustomerDetailsCardProps {
  simplified?: boolean;
  preferences?: boolean;
  supplier: Supplier;
}

export default function CustomerDetailsCard({
  simplified,
  preferences,
  supplier,
}: CustomerDetailsCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [popperNode, setPopperNode] = useState<HTMLDivElement | null>(null);
  const [contactMethodIds, setContactMethodIds] = useState<any>({
    phone_number_id: null,
    email_address_id: null,
    social_media_id: null,
  });

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);

  const isLoading = businessProfileStatus === 'loading';

  const contactMethodOptions = ['Phone number', 'Email address', 'Social media'];

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const [formData, setFormData] = useState<Partial<Supplier>>({
    id: supplier?.id,
    name: supplier?.name,
    registration_number: supplier?.registration_number,
    website: supplier?.website,
    physical_address: supplier?.physical_address,
    postal_address: supplier?.postal_address,
    industry: supplier?.industry,
    description: supplier?.description,
    supplier_type: supplier?.supplier_type,
  });

  const saveChanges = () => {
    if (!supplier?.id) return;

    const payload = {
      name: formData.name,

      registration_number: formData.registration_number,
      website: formData.website,

      physical_address: formData.physical_address,
      postal_address: formData.postal_address,
      industry: formData.industry,
      description: formData.description,
      supplier_type: formData.supplier_type,
    };

    dispatch(
      updateSupplier({
        id: supplier.id,
        company: supplier.company,
        ...payload,
      })
    );
    setIsEdited(false);
    setShowModal(false);
  };

  const confirmExit = () => {
    setIsEdited(false);
    setShowModal(false);
  };

  const handleChange =
    <K extends keyof Supplier>(field: K) =>
    (value: Supplier[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEdited &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        (!popperNode || !popperNode.contains(event.target as Node)) // Ahora ignora correctamente los clics en el menú
      ) {
        const targetElement = event.target as HTMLElement;
        if (event.target instanceof HTMLElement) {
          const ignoredElements = ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL', 'MENU-ITEM'];
          // if (ignoredElements.includes(event.target.tagName)) return;
        }

        if (
          targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
          targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
        ) {
          return;
        }
        setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited, popperNode]);

  const supplierTypeOptions = [
    'Business Partner',
    'Client',
    'Consultant',
    'Contractor',
    'Manufacturer',
    'Supplier of goods and services',
  ];

  return (
    <Grid container className='inputs-editable'>
      <Grid item xs={12}>
        <Card
          className={`${styles.card} ${isEdited ? styles.card_border : ''} ${
            preferences && styles.preferences_card
          }`}
          ref={formRef}
        >
          <CardContent
            className={isLoading ? styles.card_content_min_height : ''}
            sx={{ paddingTop: 0, paddingBottom: '0 !important' }}
          >
            <Grid container alignItems='center' flexWrap='nowrap' p={2}>
              {!preferences ? (
                <Grid container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }} item>
                    <AccountCircleOutlined />
                  </Grid>
                  <Grid item>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.customerDetails')}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }} item>
                    <RoomPreferencesOutlinedIcon />
                  </Grid>
                  <Grid item>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.supplierIndustry')}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={1} container justifyContent='flex-end'>
                {!isEdited ? (
                  <Tooltip className='flex-shrink-0' title={t('buttons.edit')} placement='top'>
                    <IconButton onClick={toggleEditing}>
                      <BrushIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    type='submit'
                    className='btn-default btn-action-basic'
                    role='button'
                    disabled={!isEdited}
                    variant='contained'
                    size='large'
                    onClick={saveChanges}
                  >
                    {t('buttons.save')}
                  </Button>
                )}
              </Grid>
            </Grid>

            {!simplified ? (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    id='supplier-name'
                    label={t('labels.supplierName')}
                    variant='outlined'
                    fullWidth
                    value={formData.name ?? ''}
                    onChange={(e) => handleChange('name')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id='registration-number'
                    label={t('labels.supplierRegistrationNumber')}
                    variant='outlined'
                    fullWidth
                    value={formData.registration_number ?? ''}
                    onChange={(e) => handleChange('registration_number')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id='website'
                    label={t('labels.website')}
                    variant='outlined'
                    fullWidth
                    value={formData.website ?? ''}
                    onChange={(e) => handleChange('website')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    id='description'
                    label={t('labels.supplierDescription')}
                    variant='outlined'
                    fullWidth
                    value={formData.description ?? ''}
                    onChange={(e) => handleChange('description')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id='physical-address'
                    label={t('formInputs.physicalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.physical_address ?? ''}
                    onChange={(e) => handleChange('physical_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id='postal-address'
                    label={t('formInputs.postalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth variant='outlined' disabled={!isEdited}>
                    <InputLabel id='supplier-type-label'>{t('labels.type')}</InputLabel>
                    <Select
                      fullWidth
                      sx={{
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                      }}
                      value={formData.supplier_type}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChange('supplier_type')(e.target.value);
                      }}
                      disabled={!isEdited}
                      displayEmpty
                      inputProps={{ 'aria-label': t('labels.type') }}
                    >
                      <MenuItem value='' disabled>
                        Select Supplier Type
                      </MenuItem>
                      {supplierTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id='industry'
                    label={t('labels.industry')}
                    variant='outlined'
                    fullWidth
                    value={formData.industry ?? ''}
                    onChange={(e) => handleChange('industry')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            ) : (
              simplified &&
              !preferences && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      id='supplier-name'
                      label={t('labels.supplierName')}
                      variant='outlined'
                      fullWidth
                      value={formData.name ?? ''}
                      onChange={(e) => handleChange('name')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id='registration-number'
                      label={t('labels.supplierRegistrationNumber')}
                      variant='outlined'
                      fullWidth
                      value={formData.registration_number ?? ''}
                      onChange={(e) => handleChange('registration_number')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id='website'
                      label={t('labels.website')}
                      variant='outlined'
                      fullWidth
                      value={formData.website ?? ''}
                      onChange={(e) => handleChange('website')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      id='description'
                      label={t('labels.supplierDescription')}
                      variant='outlined'
                      fullWidth
                      value={formData.description ?? ''}
                      onChange={(e) => handleChange('description')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                </Grid>
              )
            )}
            {simplified && preferences && (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth variant='outlined' disabled={!isEdited}>
                    <InputLabel id='supplier-type-label'>{t('labels.type')}</InputLabel>
                    <Select
                      fullWidth
                      sx={{
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                      }}
                      value={formData.supplier_type}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChange('supplier_type')(e.target.value);
                      }}
                      disabled={!isEdited}
                      displayEmpty
                      inputProps={{ 'aria-label': t('labels.type') }}
                    >
                      <MenuItem value='' disabled>
                        Select Supplier Type
                      </MenuItem>
                      {supplierTypeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id='industry'
                    label={t('labels.industry')}
                    variant='outlined'
                    fullWidth
                    value={formData.industry ?? ''}
                    onChange={(e) => handleChange('industry')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Grid>
  );
}
